import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Info, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitCheckIn } from "@/lib/api";

const FREQ_OPTS = [["never", 0], ["1–2 days", 1], ["3–5 days", 2], ["most days", 3]];
const FACTOIDS = [
  "Healthy eating principles are universal — but local foods and routines decide whether they stick.",
  "Nutrition reference values shift with age. What worked at 25 needs recalibration.",
  "Low energy has many causes. This check-in prioritizes habits, not labels.",
  "Vitamin B12 occurs naturally in animal foods — fortified foods matter on plant-based patterns.",
  "No supplement replaces the variety and balance of food.",
  "WHO guidance: weekly aerobic activity plus muscle-strengthening on two or more days.",
  "A supplement is never the first answer to persistent sleep problems.",
  "Sunlight through glass does not produce vitamin D.",
  "Supplements can interact with medicines and may be unsuitable before surgery.",
];

const Single = ({ options, value, onPick, testid }) => (
  <div className="grid sm:grid-cols-2 gap-3" data-testid={testid}>
    {options.map((o) => (
      <button key={o.v} data-testid={`${testid}-opt-${o.v}`} onClick={() => onPick(o.v)}
        className={`text-left rounded-md border px-5 py-4 min-h-[44px] transition-all duration-200 hover:-translate-y-0.5 ${
          value === o.v ? "border-clay bg-clay/10 text-ink font-semibold" : "border-ink/15 bg-bone hover:border-ink/40"
        }`}>
        <span className="text-sm sm:text-base">{o.label}</span>
        {o.sub && <span className="block text-xs text-slatem mt-1">{o.sub}</span>}
      </button>
    ))}
  </div>
);

const SegRow = ({ label, value, onPick, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-ink/10 last:border-0">
    <span className="text-sm font-medium flex-1">{label}</span>
    <div className="flex gap-1.5 flex-wrap" data-testid={testid}>
      {FREQ_OPTS.map(([l, v]) => (
        <button key={v} data-testid={`${testid}-${v}`} onClick={() => onPick(v)}
          className={`rounded-full px-3.5 py-2 text-xs font-medium border min-h-[44px] transition-colors duration-150 ${
            value === v ? "bg-ink text-bone border-ink" : "border-ink/20 hover:border-ink/50"
          }`}>{l}</button>
      ))}
    </div>
  </div>
);

export default function CheckIn() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = consent, 1..9 = questions
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState({
    market: null, age: null, goal: null, diet: null,
    foods: { veg: null, fruit: null, pulses: null, grains: null, dairy: null, animal: null, oilyfish: null },
    activity: { strength: null, moderate: null, breaks: null },
    sleep: { hours: null, refreshed: null, shift: null },
    routine: [],
    safety: null,
  });

  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));
  const setF = (k, v) => setA((p) => ({ ...p, foods: { ...p.foods, [k]: v } }));
  const setAct = (k, v) => setA((p) => ({ ...p, activity: { ...p.activity, [k]: v } }));
  const setSlp = (k, v) => setA((p) => ({ ...p, sleep: { ...p.sleep, [k]: v } }));

  const toggleRoutine = (v) =>
    setA((p) => {
      let r = p.routine.includes(v) ? p.routine.filter((x) => x !== v) : [...p.routine, v];
      if (v === "none" && !p.routine.includes("none")) r = ["none"];
      if (v !== "none") r = r.filter((x) => x !== "none");
      return { ...p, routine: r };
    });

  const canContinue = useMemo(() => {
    switch (step) {
      case 1: return !!a.market;
      case 2: return !!a.age;
      case 3: return !!a.goal;
      case 4: return !!a.diet;
      case 5: return Object.values(a.foods).every((v) => v !== null);
      case 6: return Object.values(a.activity).every((v) => v !== null);
      case 7: return a.sleep.hours !== null && a.sleep.refreshed !== null && a.sleep.shift !== null;
      case 8: return a.routine.length > 0;
      case 9: return !!a.safety;
      default: return true;
    }
  }, [step, a]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...a,
        sleep: { ...a.sleep, refreshed: a.sleep.refreshed === "yes", shift: a.sleep.shift === "yes" },
      };
      const res = await submitCheckIn(payload);
      localStorage.setItem("zb_plan_id", res.plan_id);
      localStorage.setItem("zb_plan_saved", new Date().toISOString());
      navigate(`/plan/${res.plan_id}`);
    } catch (e) {
      toast.error("Could not build your plan. Please try again.");
      setBusy(false);
    }
  };

  const next = () => (step === 9 ? submit() : setStep((s) => s + 1));

  const stepUI = () => {
    switch (step) {
      case 0:
        return (
          <div data-testid="checkin-consent">
            <div className="flex items-center gap-2.5 mb-6 text-sage"><Info className="h-5 w-5" /><span className="font-mono text-[11px] uppercase tracking-[0.25em]">before we start</span></div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-5">Test if you are <span className="italic text-clay">Zinda Banda?</span></h1>
            <p className="text-slatem leading-relaxed mb-4 max-w-xl">
              Nine quick questions about your routine, plate and goals. Your result shows how close you are —
              and the 10-point plan that gets you the rest of the way. No account, no email, no free-text health history.
            </p>
            <p className="text-sm text-slatem/80 mb-8 max-w-xl">General wellbeing information, not medical advice · 45–90 seconds · you can go back at any step.</p>
          </div>
        );
      case 1:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8">Where do you live?</h1>
            <Single testid="q1" value={a.market} onPick={(v) => { set("market", v); setTimeout(() => setStep(2), 220); }}
              options={[{ v: "uae", label: "UAE" }, { v: "india", label: "India" }, { v: "elsewhere", label: "Elsewhere" }, { v: "pnts", label: "Prefer not to say" }]} />
          </div>
        );
      case 2:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-2">Your age range?</h1>
            <p className="text-sm text-slatem mb-8">Reference values change with age — that is all we use it for.</p>
            <Single testid="q2" value={a.age} onPick={(v) => { set("age", v); setTimeout(() => setStep(3), 220); }}
              options={[{ v: "30-39", label: "30–39" }, { v: "40-49", label: "40–49" }, { v: "50-59", label: "50–59" }, { v: "60+", label: "60+" }, { v: "pnts", label: "Prefer not to say" }]} />
          </div>
        );
      case 3:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8">What would make the biggest difference right now?</h1>
            <Single testid="q3" value={a.goal} onPick={(v) => { set("goal", v); setTimeout(() => setStep(4), 220); }}
              options={[
                { v: "energy", label: "Steady energy" }, { v: "strength", label: "Strength & recovery" },
                { v: "heart", label: "Heart & metabolic habits" }, { v: "sleep", label: "Sleep & stress" },
                { v: "digestion", label: "Digestion" }, { v: "weight", label: "Healthy weight" },
                { v: "coverage", label: "Everyday coverage" },
              ]} />
          </div>
        );
      case 4:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8">How do you eat?</h1>
            <Single testid="q4" value={a.diet} onPick={(v) => { set("diet", v); setTimeout(() => setStep(5), 220); }}
              options={[
                { v: "mixed", label: "Mixed — everything" }, { v: "veg_dairy", label: "Vegetarian with dairy / eggs" },
                { v: "vegan", label: "Vegan" }, { v: "pescatarian", label: "Pescatarian" },
                { v: "irregular", label: "Often irregular" }, { v: "prescribed", label: "Medically prescribed diet" },
              ]} />
          </div>
        );
      case 5:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-2">How often do these show up in your week?</h1>
            <p className="text-sm text-slatem mb-6">Honest beats impressive. This drives the food-first plan.</p>
            <div className="rounded-2xl border border-ink/15 bg-bone px-5 py-2">
              <SegRow label="Vegetables" testid="q5-veg" value={a.foods.veg} onPick={(v) => setF("veg", v)} />
              <SegRow label="Fruit" testid="q5-fruit" value={a.foods.fruit} onPick={(v) => setF("fruit", v)} />
              <SegRow label="Pulses / beans (dal, chana, hummus)" testid="q5-pulses" value={a.foods.pulses} onPick={(v) => setF("pulses", v)} />
              <SegRow label="Whole grains (roti, oats, brown rice)" testid="q5-grains" value={a.foods.grains} onPick={(v) => setF("grains", v)} />
              <SegRow label="Dairy or fortified alternative" testid="q5-dairy" value={a.foods.dairy} onPick={(v) => setF("dairy", v)} />
              <SegRow label="Eggs / meat / fish" testid="q5-animal" value={a.foods.animal} onPick={(v) => setF("animal", v)} />
              <SegRow label="Oily fish (sardines, mackerel, salmon)" testid="q5-oilyfish" value={a.foods.oilyfish} onPick={(v) => setF("oilyfish", v)} />
            </div>
          </div>
        );
      case 6:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8">How do you move?</h1>
            <div className="space-y-7">
              <div>
                <p className="text-sm font-medium mb-3">Strength sessions per week</p>
                <Single testid="q6-strength" value={a.activity.strength} onPick={(v) => setAct("strength", v)}
                  options={[{ v: 0, label: "None" }, { v: 1, label: "1–2" }, { v: 2, label: "3+" }]} />
              </div>
              <div>
                <p className="text-sm font-medium mb-3">Moderate activity (brisk walks, cycling, sport)</p>
                <Single testid="q6-moderate" value={a.activity.moderate} onPick={(v) => setAct("moderate", v)}
                  options={[{ v: 0, label: "Under 1 hr / week" }, { v: 1, label: "1–3 hrs" }, { v: 2, label: "3+ hrs" }]} />
              </div>
              <div>
                <p className="text-sm font-medium mb-3">Breaks from sitting during the day</p>
                <Single testid="q6-breaks" value={a.activity.breaks} onPick={(v) => setAct("breaks", v)}
                  options={[{ v: 0, label: "Rarely" }, { v: 1, label: "Sometimes" }, { v: 2, label: "Often" }]} />
              </div>
            </div>
          </div>
        );
      case 7:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8">How do you sleep?</h1>
            <div className="space-y-7">
              <div>
                <p className="text-sm font-medium mb-3">Hours per night</p>
                <Single testid="q7-hours" value={a.sleep.hours} onPick={(v) => setSlp("hours", v)}
                  options={[{ v: 0, label: "Under 6" }, { v: 1, label: "6–7" }, { v: 2, label: "7–8" }, { v: 3, label: "8+" }]} />
              </div>
              <div>
                <p className="text-sm font-medium mb-3">Do you wake up refreshed?</p>
                <Single testid="q7-refreshed" value={a.sleep.refreshed} onPick={(v) => setSlp("refreshed", v)}
                  options={[{ v: "yes", label: "Most mornings" }, { v: "no", label: "Rarely" }]} />
              </div>
              <div>
                <p className="text-sm font-medium mb-3">Shift work?</p>
                <Single testid="q7-shift" value={a.sleep.shift} onPick={(v) => setSlp("shift", v)}
                  options={[{ v: "no", label: "No" }, { v: "yes", label: "Yes" }]} />
              </div>
            </div>
          </div>
        );
      case 8:
        return (
          <div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-2">What describes your routine?</h1>
            <p className="text-sm text-slatem mb-8">Select all that apply.</p>
            <div className="grid sm:grid-cols-2 gap-3" data-testid="q8">
              {[
                { v: "indoors", label: "Mostly indoors" }, { v: "low_sun", label: "Limited direct sun" },
                { v: "travel", label: "Frequent travel" }, { v: "heat", label: "High-heat outdoor activity" },
                { v: "alcohol", label: "Alcohol regularly" }, { v: "smoker", label: "I smoke" },
                { v: "none", label: "None of these" },
              ].map((o) => (
                <button key={o.v} data-testid={`q8-opt-${o.v}`} onClick={() => toggleRoutine(o.v)}
                  className={`text-left rounded-md border px-5 py-4 min-h-[44px] text-sm sm:text-base transition-all duration-200 hover:-translate-y-0.5 ${
                    a.routine.includes(o.v) ? "border-clay bg-clay/10 font-semibold" : "border-ink/15 bg-bone hover:border-ink/40"
                  }`}>{o.label}</button>
              ))}
            </div>
          </div>
        );
      case 9:
        return (
          <div>
            <div className="flex items-start gap-3 rounded-2xl border border-clay/30 bg-clay/5 p-4 mb-8" data-testid="q9-explainer">
              <ShieldAlert className="h-5 w-5 text-clay shrink-0 mt-0.5" />
              <p className="text-sm text-ink/80 leading-relaxed">
                One safety flag keeps the plan honest. We only need a Yes or No — no names, no details. A “yes” switches
                your plan to clinician-safe mode: food, movement and sleep foundations stay, supplement suggestions disappear.
              </p>
            </div>
            <h1 className="font-serif text-3xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.98] mb-8 leading-snug">
              Any prescription medicine, kidney / liver / heart condition, bleeding disorder, upcoming surgery, or unexplained symptoms?
            </h1>
            <Single testid="q9" value={a.safety} onPick={(v) => set("safety", v)}
              options={[{ v: "no", label: "No" }, { v: "yes", label: "Yes" }, { v: "pnts", label: "Prefer not to say" }]} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <main className="min-h-screen pt-28 pb-20 px-4 sm:px-6" data-testid="checkin-page">
      <div className="mx-auto max-w-2xl">
        {/* progress rail */}
        <div className="flex items-center gap-4 mb-10">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slatem" data-testid="checkin-progress-label">
            {step === 0 ? "Intro" : `Step ${step} of 9`}
          </span>
          <div className="flex-1 h-1 rounded-full bg-ink/10 overflow-hidden">
            <motion.div className="h-full bg-clay rounded-full" animate={{ width: `${(step / 9) * 100}%` }} transition={{ duration: 0.4, ease: "easeOut" }} data-testid="checkin-progress-bar" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 28 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -28 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}>
            {stepUI()}
          </motion.div>
        </AnimatePresence>

        {step >= 1 && (
          <div className="mt-8 rounded-2xl bg-sand border border-ink/10 px-5 py-4" data-testid="checkin-factoid">
            <p className="text-xs text-slatem leading-relaxed"><span className="font-mono uppercase tracking-[0.18em] text-sage mr-2">fact</span>{FACTOIDS[step - 1]}</p>
          </div>
        )}

        <div className="mt-10 flex items-center justify-between gap-4">
          {step > 0 ? (
            <button onClick={() => setStep((s) => s - 1)} data-testid="checkin-back-button"
              className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-ink transition-colors min-h-[44px]">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          <button onClick={next} disabled={!canContinue || busy} data-testid="checkin-next-button"
            className="inline-flex items-center gap-2.5 rounded-md bg-ink text-bone px-7 py-3.5 text-sm font-bold uppercase tracking-wider transition-all duration-200 hover:bg-clay disabled:opacity-35 disabled:pointer-events-none min-h-[44px]">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {step === 0 ? "Start the test" : step === 9 ? "See my result" : "Continue"}
            {!busy && <ArrowRight className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </main>
  );
}
